import cn from 'classnames';
import { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import { ProgressBarContext, UserContext } from 'components/signUpFunnel/Contexts';
import DietaryPreferencesForm from 'components/signUpFunnel/shared/DietaryPreferencesForm';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import { getBrandRoute } from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import { STANDARD, FAMILY, Statuses, GLUTEN_SMART } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import {
  createEvents,
  updatePreferences,
  getPreferences,
} from 'services/RequestService/endpoints/signUp';
import styles from 'styles/components/signUpFunnel/preferences/PreferencesContainer';

import MenuGallery from './MenuGallery';
import PreferencesFormSkeleton from './PreferencesFormSkeleton';

const PreferencesDietaryContainer = () => {
  const [preferences, setPreferences] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allergyIds, setAllergyIds] = useState(null);
  const [dietaryPreferenceIds, setDietaryPreferenceIds] = useState(null);

  const { user, setUser } = useContext(UserContext);

  const { setProgress } = useContext(ProgressBarContext);
  const { brand } = useContext(BrandContext);

  const navigate = useNavigate();
  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;
  const isStandardMealPlan = mealPlanSlug === STANDARD;

  const glutenSmart = preferences?.dietaryPreferences?.some(
    pref => pref.friendlyName === GLUTEN_SMART
  );

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].preferencesDietary.viewed);
      }
    },
    [brand]
  );

  useEffect(() => {
    getPreferences().then(response => {
      if (response.ok) {
        setPreferences(response.preferences);
      }
    });
  }, []);

  useEffect(() => {
    if (preferences?.dietaryPreferences) {
      setDietaryPreferenceIds(
        preferences.dietaryPreferences
          .filter(dietaryPreference => dietaryPreference.selected === true)
          .map(dietaryPreference => dietaryPreference.id)
      );
    }
  }, [preferences?.dietaryPreferences]);

  useEffect(() => {
    if (preferences?.allergies) {
      setAllergyIds(
        preferences.allergies
          .filter(allergy => allergy.selected === true)
          .map(allergy => allergy.id)
      );
    }
  }, [preferences?.allergies]);

  const handlePreferencesClick = ({ friendlyName, selected }) => {
    const newDietaryPreferenceState = preferences.dietaryPreferences.map(dietaryPreference => {
      if (dietaryPreference.friendlyName === friendlyName) {
        return { ...dietaryPreference, selected: !selected };
      }
      return dietaryPreference;
    });

    setPreferences({ dietaryPreferences: newDietaryPreferenceState });
  };

  const handleOnClick = useAnalyticsCallback(
    (allergyIds, dietaryPreferenceIds) => {
      setIsSubmitting(true);
      updatePreferences({ params: { allergyIds, dietaryPreferenceIds } })
        .then(response => {
          setIsSubmitting(false);
          if (response.ok && response.preferences?.dietaryPreferences) {
            setUser(prevUser => ({
              ...prevUser,
              status: Statuses.DIETARY_AVOID_STEP,
              dietaryPreferenceNames: response.preferences.dietaryPreferences
                .filter(pref => pref.selected === true)
                .map(pref => pref.friendlyName),
              optIns: response.optIns,
            }));
          }
          navigate(getBrandRoute({ brand: brand, isFamily: isFamilyMealPlan }).preferenceAvoid);
        })
        .catch(error => {
          return error;
        });
    },
    ({ logEvent, events }) => {
      const dietaryPreferences = preferences.dietaryPreferences
        .filter(preference => preference.selected)
        .map(preference => preference.friendlyName);

      if (brand) {
        logEvent(events.signUp[brand].preferencesDietary.submitted, {
          dietaryPreferences,
          mealPlanSlug: mealPlanSlug,
        });
      }
    },
    [preferences, brand, mealPlanSlug]
  );

  useMetadata({
    title: `Step 2: Preferences | ${brandData(brand)?.displayName}`,
  });

  useEffect(() => {
    setProgress({ percent: '30' });

    createEvents({ params: { event_name: 'PageView' } }).catch(error =>
      ErrorLogging.captureException(error.message)
    );
  }, []);

  return (
    <>
      {user && preferences?.dietaryPreferences ? (
        <div>
          <DietaryPreferencesForm
            header="What are you looking for?"
            subheader="You'll still have access to the full menu."
            gridClassName={isStandardMealPlan ? 'bp1:grid-cols-2' : ''}
            preferences={preferences}
            handlePreferencesClick={handlePreferencesClick}
            containerClassName={cn(styles[brand], styles.preferencesContainer)}
          />
          <div className={cn(styles[brand], styles.preferencesContainer)}>
            <Button
              type="submit"
              className="button mx-auto w-full bp1:mt-8"
              onClick={() => handleOnClick(allergyIds, dietaryPreferenceIds)}
              isSubmitting={isSubmitting}
              variation="Pill"
            />
            {glutenSmart && (
              <Text
                brandStylesOverrides="textSm"
                className="pt-4 text-center text-sm leading-5 text-charcoal-100 bp1:text-left"
              >
                *Gluten-Smart meals are not suitable for those with Celiac disease or severe Gluten
                Intolerance
              </Text>
            )}
          </div>
          <MenuGallery />
        </div>
      ) : (
        <PreferencesFormSkeleton skeletonClassName="p-4 bp2:p-6 bg-white-100" skeletonCount={5} />
      )}
    </>
  );
};

export default PreferencesDietaryContainer;
